import { END_USER_URL } from '../config';
/**
 * Shared Open Graph / Twitter Card helpers for Faster Link (endUser).
 * Social crawlers prefer absolute https image URLs and a correct twitter:card type.
 */

export const SITE_NAME = 'Faster Link';
export const DEFAULT_ORIGIN = `${END_USER_URL}`;
export const DEFAULT_OG_IMAGE_PATH = '/web-app-manifest-512x512.png';

/** Landing page only — store pages use buildStoreSeo() instead. */
export const LANDING_SEO_TITLE =
  'منصة فاستر لينك Faster Link | متجر إلكتروني، منيو إلكتروني، بطاقة عمل رقمية';
export const LANDING_SEO_DESCRIPTION =
  'اعرض أعمالك ومنتجاتك باحترافية لعملائك من خلال أبليكيشن ويب باسم ولوجو علامتك التجارية. متجر إلكتروني، منيو وكتالوج إلكتروني، وبطاقة عمل رقمية. احصل على التجربة المجانية.';

/**
 * Public site origin used in og:url / og:image.
 * Prefer production origin so react-snap (localhost) does not bake local URLs into HTML.
 */
export function getOrigin() {
  const envOrigin = process.env.REACT_APP_PUBLIC_ORIGIN || process.env.REACT_APP_SITE_URL;
  if (envOrigin) return String(envOrigin).replace(/\/$/, '');

  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    if (/localhost|127\.0\.0\.1/i.test(origin)) {
      return DEFAULT_ORIGIN;
    }
    return origin;
  }
  return DEFAULT_ORIGIN;
}

export function getAbsoluteUrl(pathOrUrl, origin = getOrigin()) {
  if (!pathOrUrl) return '';
  const value = String(pathOrUrl).trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) {
    // Rewrite accidental localhost absolute URLs (e.g. from prerender) to public origin
    try {
      const parsed = new URL(value);
      if (/localhost|127\.0\.0\.1/i.test(parsed.hostname)) {
        return `${origin}${parsed.pathname}${parsed.search}`;
      }
    } catch (_) {
      /* ignore */
    }
    return value;
  }
  if (value.startsWith('//')) return `https:${value}`;
  const base = origin.replace(/\/$/, '');
  return `${base}${value.startsWith('/') ? '' : '/'}${value}`;
}

export function getCurrentPageUrl() {
  const origin = getOrigin();
  if (typeof window !== 'undefined' && window.location) {
    return `${origin}${window.location.pathname}${window.location.search}`;
  }
  return origin;
}

/**
 * Prefer cover image, then logo, then Faster Link branding image.
 */
export function getStoreShareImage(store) {
  if (!store) return getAbsoluteUrl(DEFAULT_OG_IMAGE_PATH);

  const cover = store.cover;
  let coverUrl = '';
  if (Array.isArray(cover) && cover.length > 0) {
    const first = cover[0];
    coverUrl = typeof first === 'string' ? first : (first?.full_path || first?.url || first?.image || '');
  } else if (typeof cover === 'string') {
    coverUrl = cover;
  }

  const logoUrl = store.logo || '';
  const bgUrl = store?.super_faster_settings?.background_image?.image || '';

  const candidate = coverUrl || logoUrl || bgUrl || DEFAULT_OG_IMAGE_PATH;
  return getAbsoluteUrl(candidate);
}

export function buildLandingSeo({
  title = LANDING_SEO_TITLE,
  description = LANDING_SEO_DESCRIPTION,
  imagePath = DEFAULT_OG_IMAGE_PATH,
  url,
} = {}) {
  const pageUrl = url || getCurrentPageUrl();
  const image = getAbsoluteUrl(imagePath);
  return {
    title: title || LANDING_SEO_TITLE,
    description: description || LANDING_SEO_DESCRIPTION,
    image,
    url: pageUrl,
    type: 'website',
    siteName: SITE_NAME,
    twitterCard: 'summary_large_image',
  };
}

export function buildStoreSeo(store, { url } = {}) {
  const name = store?.name || SITE_NAME;
  const description =
    store?.description ||
    store?.full_description ||
    store?.full_description_en ||
    store?.full_description_ar ||
    '';

  return {
    title: name,
    description,
    image: getStoreShareImage(store),
    url: url || getCurrentPageUrl(),
    type: 'website',
    siteName: SITE_NAME,
    twitterCard: 'summary_large_image',
  };
}

export function upsertMetaTag(name, content, attribute = 'name') {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content || '');
}

export function upsertLinkTag(rel, href) {
  if (typeof document === 'undefined') return;
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href || '');
}

/**
 * Apply OG/Twitter tags via direct DOM updates (works with react-snap after data load).
 */
export function applySeoMeta(seo) {
  if (!seo || typeof document === 'undefined') return;

  document.title = seo.title || SITE_NAME;

  upsertMetaTag('description', seo.description);
  upsertMetaTag('robots', 'index, follow');

  upsertMetaTag('og:title', seo.title, 'property');
  upsertMetaTag('og:description', seo.description, 'property');
  upsertMetaTag('og:type', seo.type || 'website', 'property');
  upsertMetaTag('og:url', seo.url, 'property');
  upsertMetaTag('og:image', seo.image, 'property');
  upsertMetaTag('og:image:secure_url', seo.image, 'property');
  upsertMetaTag('og:image:alt', seo.title, 'property');
  upsertMetaTag('og:site_name', seo.siteName || SITE_NAME, 'property');

  upsertMetaTag('twitter:card', seo.twitterCard || 'summary_large_image');
  upsertMetaTag('twitter:title', seo.title);
  upsertMetaTag('twitter:description', seo.description);
  upsertMetaTag('twitter:image', seo.image);

  upsertLinkTag('canonical', seo.url);

  // Marker for prerender tools / debugging that dynamic SEO has been applied
  document.documentElement.setAttribute('data-seo-ready', 'true');
  document.documentElement.setAttribute('data-seo-title', seo.title || '');
}
